/**
 * Users API endpoints
 *
 * API contract: see 001-backend-architecture.md
 */

import { api } from '../client';
import type { UpdateProfileRequest } from '../types';
import { UserResponseSchema } from '../schemas';
import { parseResponse } from '../validation';
import { randomUUID } from '@/lib/uuid';
import { MIN_USER_SEARCH_QUERY_LENGTH } from '@/lib/constants';

// User search result — used by sharing dialogs, admin, anywhere
// that needs to find a user by name or email.
export interface UserSearchResult {
  id: string;
  name: string | null;
  role: string | null;
  specialisation: string | null;
  institution: string | null;
  unit: string | null;
  // Contact PII, gated server-side: present only when the caller holds
  // platform-admin (the admin pickers); absent for the general directory.
  email?: string | null;
}

/** searchUsers' result: the matched page plus the honest total, so a
 *  capped (non-admin) search can tell its caller "there's more" instead of
 *  reading as "that's everyone". totalCount is null only on a malformed/
 *  missing X-Total-Count response (server invariant violation, logged
 *  server-side) — callers should treat null as "unknown", not "no more". */
export interface UserSearchWithTotal {
  results: UserSearchResult[];
  totalCount: number | null;
}

/**
 * Search users by name or email prefix.
 * Rate-limited on the backend to prevent directory scraping. The backend
 * caps this (non-admin) path at a configured default/max page size — see
 * X-Total-Count in HandleListUsers (api-service-go/handlers.go) — so
 * totalCount can exceed results.length; callers that show suggestions to a
 * human should surface that (see ShareDialog/ShiftNursePicker's hint row).
 */
export async function searchUsers(query: string): Promise<UserSearchWithTotal> {
  if (query.length < MIN_USER_SEARCH_QUERY_LENGTH) {
    return { results: [], totalCount: null };
  }
  const { items, totalCount } = await api.getJSONWithTotal<UserSearchResult>(
    `/users?q=${encodeURIComponent(query)}`,
  );
  return { results: items, totalCount };
}

export const usersApi = {
  /**
   * GET /users — full user directory for admin pickers (replaces the removed
   * /admin/users). No limit param: the backend returns the COMPLETE list to
   * callers who hold can_view_contact (platform-admin, which every admin
   * picker caller does) and is uncapped only for THAT branch — everyone else
   * is capped (server-configured default/max) to deter directory scraping,
   * and that capped page truncates silently as far as this array return goes.
   * The real total travels as the X-Total-Count response header (not
   * surfaced by the thin `api.get` wrapper here) and the MCP list_users tool
   * reshapes it into an explicit total_count/has_more envelope for agent
   * callers; this admin-only helper doesn't need it because its caller is
   * never on the capped branch.
   */
  listAll: (): Promise<UserSearchResult[]> =>
    api.get<UserSearchResult[]>('/users'),

  /**
   * GET /me — Get current user profile
   */
  getMe: () =>
    api.get('/me').then(
      (res) => parseResponse(UserResponseSchema, res, 'GET /me'),
    ),

  /**
   * PUT /me — Update current user profile.
   *
   * Carries a fresh Idempotency-Key per call: the handler runs an ACL + event
   * saga, so a network replay must not duplicate the user-record version events
   * or re-run the org grant writes. One key per logical save; the api client
   * reuses the same headers across its own network retries.
   */
  updateMe: (data: UpdateProfileRequest) =>
    api
      .put<unknown>('/me', data, { headers: { 'Idempotency-Key': randomUUID() } })
      .then((res) => parseResponse(UserResponseSchema, res, 'PUT /me')),

  /**
   * PUT /me/twin — Update digital twin name only
   */
  updateTwinName: (twinName: string) =>
    api.put('/me/twin', { twin_name: twinName }).then(
      (res) => parseResponse(UserResponseSchema, res, 'PUT /me/twin'),
    ),
};
